import { Config } from "@config";
import type { MeasureComponentProps, MeasureComponentState } from "@component/MeasureComponent";
import { MeasureComponent } from "@component/MeasureComponent";
import { faChevronRight as fasChevronRight, faStar as fasStar } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import { http, PromisePool } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Alert } from "./Alert";
import { Button } from "./Button";
import { LazyLoad } from "./LazyLoad";
import "slick-carousel/slick/slick.scss";
import "slick-carousel/slick/slick-theme.scss";

const Slider = React.lazy(() => import("react-slick"));

type LeadFormTypes = "BM_HQ_MORTGAGE" | "BM_HQ_PERSONAL_LOAN" | "BM_HQ_RETAIL_ACCOUNT" | "BM_HQ_BABY_LOAN";

interface Expert {
	allQuestionsAnswered?: number;
	exteriorId: string;
	feedbackCount?: number;
	userId: string;
	userName: string;
}
interface State extends MeasureComponentState {
	experts: Expert[];
	error: boolean;
	offsetId: number;
}

interface Props extends MeasureComponentProps {
	groupName: LeadFormTypes;
}

export class ExpertSlider extends MeasureComponent<Props, State> {
	protected productTypes = "";
	protected componentType = "listing";
	private readonly promisePool = new PromisePool();
	private settings = {
		dots: false,
		infinite: true,
		speed: 500,
		slidesToScroll: 1,
		slidesToShow: 4,
		nextArrow: <FontAwesomeIcon icon={fasChevronRight} />,
		prevArrow: <FontAwesomeIcon icon={fasChevronRight} />,
		afterChange: (index) => {
			this.setState({
				offsetId: index,
			});

			this.swipeCount += 1;

			this.sendSwipeEvent();
		},
		responsive: [
			{
				breakpoint: 1024,
				settings: {
					slidesToShow: 3,
					slidesToScroll: 1,
					infinite: true,
				},
			},
			{
				breakpoint: 767,
				settings: {
					slidesToShow: 2,
					slidesToScroll: 1,
					speed: 500,
				},
			},
			{
				breakpoint: 480,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1,
					speed: 500,
				},
			},
		],
	};
	private swipeCount = 0;

	public constructor(props: Props) {
		super(props);

		this.state = {
			experts: [],
			error: false,
			offsetId: 0,
			wasVisible: false,
		};
	}

	private sendSwipeEvent(): void {
		let currentWidthslidesToShow = this.settings.slidesToShow;
		const currentWidth = window.innerWidth;

		this.settings.responsive.forEach((res) => {
			if (currentWidth < res.breakpoint) {
				currentWidthslidesToShow = res.settings.slidesToShow;
			}
		});

		this.sendEvent("BM_advisorSwipe", {
			screenFit: currentWidthslidesToShow,
		});
	}

	public componentDidMount(): void {
		const { groupName } = this.props;

		this.promisePool
			.add(http.getWithCache(Config.getWpRestApiUrl(`/consultant-review/${encodeURIComponent(groupName)}`)))
			.then((experts) => {
				this.setState({
					experts: experts.sort(() => Math.random() - 0.5),
					error: false,
				});
			})
			.catch(() => {
				this.setState({
					experts: [],
					error: true,
				});
			});
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	public render(): ReactNode {
		const { experts, error } = this.state;

		this.settings.slidesToShow = Math.min(experts.length, 4);

		if (Config.isSsr()) {
			return null;
		}

		if (error) {
			return (
				<div>
					<Alert icon="auto" size="lg" variant="danger">
						Váratlan hiba az adatok betöltése közben!
					</Alert>
				</div>
			);
		}

		return (
			<div>
				<LazyLoad>
					<Slider {...this.settings}>
						{experts.map((expert, expertId) => this.renderExpertData(expert, expertId))}
					</Slider>
				</LazyLoad>
			</div>
		);
	}

	private renderExpertData(expert: Expert, expertId: number): ReactNode {
		const { groupName } = this.props;
		const allQuestionsAnswered = expert.allQuestionsAnswered || 0;
		const width = allQuestionsAnswered * 20;

		return (
			<div className="expert-parent position-relative my-3" key={expert.userId}>
				<div className="expert-shadow-box mx-2 p-3 rounded">
					<div className="expert text-center">
						<img
							src={`/wp-content/themes/bankmonitor/img/avatar/128/${expert.userId.toLowerCase()}.jpg`}
							alt={expert.userName}
							height="100"
							width="100"
							className="rounded-circle mx-auto border"
							loading="lazy"
						/>
						<div className="h4 mt-2 mb-3">{expert.userName || " "}</div>
					</div>
					<div className="text-center" style={{ maxWidth: 230, margin: "0 auto" }}>
						<div
							className="stars rating d-inline-flex position-absolute"
							style={{ paddingTop: 5, zIndex: -1, color: "#ffe9a6" }}
						>
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
						</div>
						<div
							className="stars text-warning rating d-inline-flex overflow-hidden"
							style={{ width, paddingTop: 5 }}
						>
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
							<FontAwesomeIcon icon={fasStar} />
						</div>
						<div className="h4 mb-0 text-center">{allQuestionsAnswered.toFixed(1) || "?"}/5</div>
						<div className="small mt-0 text-center">{expert.feedbackCount} visszajelzés</div>
						<Button
							variant="primary"
							size="sm"
							className="mt-4 mx-auto stretched-link"
							onClick={() => this.handleExpertDataPageButtonClick(expert, expertId)}
							href={Config.getBankmonitorUrl(`/szakertok/${encodeURIComponent(expert.exteriorId)}`, {
								groupName: groupName,
								source: "BM",
							})}
							outlined
							block
						>
							Adatlap
						</Button>
					</div>
				</div>
			</div>
		);
	}

	@autobind
	private handleExpertDataPageButtonClick(expert: Expert, expertId: number): void {
		const { offsetId } = this.state;
		const currentWidth = window.innerWidth;
		let currentWidthslidesToShow = this.settings.slidesToShow;

		this.settings.responsive.forEach((res) => {
			if (currentWidth < res.breakpoint) {
				currentWidthslidesToShow = res.settings.slidesToShow;
			}
		});

		this.sendEvent("BM_advisorClick", {
			buttonId: `expert-interest-button-${expert.userId}`,
			advisor: expert.userId,
			reviewValue: expert.allQuestionsAnswered,
			reviewCount: expert.feedbackCount,
			swipeCount: this.swipeCount,
			advisorPosition: expertId - offsetId,
			screenFit: currentWidthslidesToShow,
		});
	}

	protected getComponentName(): string {
		return "ExpertSlider";
	}
}
